import { html, render, nothing } from "lit-html";
import { IDetailedCartInfo, IDetailedCartInfoForUi } from "../../interfaces/detailed-cart-info.js";
import { TempOrderLineProductContractUiHelper, GroupPosition } from "../../interfaces/temp-order-line-product-contract.js";
import { OrderLineType } from "../../../../../components-shared/enums/order-line-type.js";
import { SiteCartController } from "../../site-cart-controller.js";
import { Locale } from "../../../../common/interfaces/locate.js";
import { determineTranslation } from "../../../../utils/language.js";
import { cartEventSubscriber } from "../../../../common/pubsub/cart-event-pub-sub.js";
//The Vipps checkout button was not defined by BA+Desinger to be used in sitecart sidebar! Don't add it here!
export class CartSidebarOrderLines extends HTMLElement {
    constructor() {
        super();
    }

    /* #region Translations */
    private translations: ITranslationSet = {
        [Locale.En]:{
            btnRemove: "Remove",
            quantity: "Quantity",
            incVat: "Inc. TVA",
            excVat: "Exc. TVA",
        },
        [Locale.No]: {
            btnRemove: "Fjern",
            quantity: "Antall",
            incVat: "Inkl. MVA",
            excVat: "Eks. MVA",
        }
    };
    private labels = determineTranslation(this.translations) as ISiteCartSidebarOrderLinesLabels;
    /* #endregion */

    /* #region Markup */
    private mainTemplate = (orderLines: TempOrderLineProductContractUiHelper[]) => {
        return html`
        <div class="orders-list">
            <table>
                ${orderLines.map(line =>
                    html`
                        <tr class="${this._getTrCssClass(line)}" data-line-type="${line.lineType}">
                            <td class="td-image">${line.imageUrl ? html`<img src="${window.R + line.imageUrl}" width="${this._getImageSize(line)}" height="${this._getImageSize(line)}" />` : nothing }</td>
                            <td class="td-details ${!line.cantBeRemoved ? 'has-close-btn' : ''}">
                                ${this.removeButtonTemplate(line)}
                                <div class="name ${this._getFontSizeCssClass(line)} ${this._getFontColorCssClass(line)}">
                                    ${line.lineType == OrderLineType.InscriptionTextLine ?
                                        line.productName
                                        : html`<a href="${window.R + line.productUrl}">${line.productName}</a>`}
                                </div>
                                ${this._showLinePriceCondition(line) ? html`
                                <div class="price-quantity-container d-flex align-items-baseline justify-content-between ${this._getMartinTopCssClass(line)}">
                                    ${this.quantityTemplate(line)}
                                    ${this.priceTemplate(line)}
                                </div>
                                ` : nothing }
                            </td>
                        </tr>
                    `
                )}
            </table>
        </div>
    `};

    private quantityTemplate = (line: TempOrderLineProductContractUiHelper) => this._showQuantityCondition(line) ? html`<div class="quantity-unit">${this.labels.quantity}: ${line.quantity} ${line.unit ? line.unit.toLowerCase() : ''}</div>` : nothing;

    private priceTemplate = (line: TempOrderLineProductContractUiHelper) => line.linePrice > 0 ? html`<div class="${this._getSemiboldCssClass(line)} ${this._getFontSizeCssClass(line)} ${line.lineDiscountPercent > 0 || line.lineDiscount > 0 ? 'color-discount' : ''}"><span class="price text-nowrap">${line.formattedLinePrice}</span></div>` : nothing;

    private removeButtonTemplate = (line: TempOrderLineProductContractUiHelper) => this._removeButtonCondition(line) ? html`<button type="button" @click=${(e:MouseEvent) => this._siteCartController.removeLine(line)} class="btn remove" title="${this.labels.btnRemove}">
    <svg xmlns="http://www.w3.org/2000/svg" width="7.001" height="7.002" viewBox="0 0 7.001 7.002">
    <path id="x" d="M4.646,4.646a.5.5,0,0,1,.708,0L8,7.293l2.646-2.647a.5.5,0,0,1,.708.708L8.707,8l2.647,2.646a.5.5,0,0,1-.708.708L8,8.707,5.354,11.354a.5.5,0,1,1-.708-.708L7.293,8,4.646,5.354a.5.5,0,0,1,0-.708Z" transform="translate(-4.499 -4.499)" fill="#222226"/>
    </svg>
    </button>` : nothing;
    /* #endregion */

    /* #region Private */
    private _siteCartController = new SiteCartController();

    private _showLinePriceCondition(orderLine: TempOrderLineProductContractUiHelper){
        return orderLine.quantity && orderLine.linePrice > 0 && orderLine.lineType !== OrderLineType.InscriptionTextLine;
    }

    private _showQuantityCondition(orderLine: TempOrderLineProductContractUiHelper){
        return orderLine.quantity;
    }

    private _removeButtonCondition(orderLine: TempOrderLineProductContractUiHelper){
        return !orderLine.cantBeRemoved  && orderLine.lineType !== OrderLineType.InscriptionTextLine;
    }

    private _getImageSize(orderLine: TempOrderLineProductContractUiHelper){
        return orderLine.grouping?.isChild ? 54 : 60;
    }

    private _getTrCssClass(orderLine: TempOrderLineProductContractUiHelper){
        var classNames =  orderLine.grouping && orderLine.grouping?.position !== GroupPosition.Last ? CssClasses.HiddenTrBottomBorder : '';
        classNames += orderLine.grouping?.isChild ? ' group-child' : '';
        return classNames;
    }

    private _getMartinTopCssClass(orderLine: TempOrderLineProductContractUiHelper){
        return orderLine.grouping?.isChild ? 'mt-sm' : 'mt-md';
    }

    private _getFontSizeCssClass(orderLine: TempOrderLineProductContractUiHelper){
        return orderLine.grouping?.isChild ? CssClasses.FontSizeSmall : '';
    }

    private _getFontColorCssClass(orderLine: TempOrderLineProductContractUiHelper){
        return orderLine.grouping?.isChild ? CssClasses.TextGrayColor : '';
    }

    private _getSemiboldCssClass(orderLine: TempOrderLineProductContractUiHelper){
        return orderLine.grouping?.isChild ? '' : CssClasses.SemiboldFont;
    }

    private _updateView(data: IDetailedCartInfoForUi) {
        render(this.mainTemplate(data.products), this);
    }

    private async _onSiteCartChanged(topic: string, data: IDetailedCartInfo){
        this._updateView(data);
    }

    private async _render() {
        const data = this._siteCartController.getSiteCartInfo();
        this._updateView(data);
    }
    /* #endregion */

    public connectedCallback() {
        if(this.isConnected){
            this._render();
            cartEventSubscriber.onSiteCartChanged(this._onSiteCartChanged.bind(this));
        }
    }

    // disconnectedCallback() : void {
    // }
}

customElements.define('cart-sidebar-v1-order-lines', CartSidebarOrderLines);

/* #region Local interfaces */
interface ISiteCartSidebarOrderLinesLabels{
    btnRemove: string;
    quantity: string;
    incVat: string;
    excVat: string;
}

interface ITranslationSet {
    [key:string]: ISiteCartSidebarOrderLinesLabels
}

enum CssClasses {
    SemiboldFont = 'fw-semibold',
    HiddenTrBottomBorder = 'border-bottom-none',
    FontSizeSmall = 'fs-sm',
    TextGrayColor = 'text-secondary'
}
/* #endregion */